/**
 * realtime-helpers.js — pure helpers extracted from realtime.js so they
 * can be unit-tested in isolation (#126). No Supabase client import,
 * no module-level state, no store side effects. Just functions that
 * take data in and return data out.
 *
 * realtime.js re-imports from here so production code keeps calling
 * the same entry points — the extraction is purely for testability.
 */

import { MESSAGE_STATUS } from '../shared/constants.js';

/**
 * Defensive fallback matcher for the "broadcast arrived before HTTP
 * response" race. Under the normal Phase B flow (#135, #136), every
 * outbound row carries a deterministic correlation_id and the client
 * reconciles via exact clientId lookup in O(1). This helper is only
 * consulted when the correlation_id path misses — usually because
 * an older client build (pre-#136) sent the row, or the HTTP response
 * was lost mid-flight.
 *
 * Matching strategy (tightest first):
 *   1. Type must match.
 *   2. Timestamps must be within TS_WINDOW seconds of each other.
 *   3. For text: body content must match exactly.
 *   4. For media: prefer filename match, then caption match.
 *   5. Fallback: oldest pending by timestamp (FIFO).
 *
 * @param {Array<object>} thread  the thread's current messages
 * @param {object}        row     the incoming broadcast row
 * @returns {object|null}         the matched pending message, or null
 */
export function findPendingOutboundMatch(thread, row) {
  const TS_WINDOW = 15;
  const candidates = thread.filter(m =>
    m.status === MESSAGE_STATUS.PENDING &&
    m.type === row.type &&
    Math.abs((m.timestamp || 0) - (row.timestamp || 0)) < TS_WINDOW
  );
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  // Multiple pending of the same type — disambiguate by content.
  if (row.type === 'text') {
    const body = row.body?.text?.body || '';
    const exact = candidates.find(m => (m.text?.body || '') === body);
    if (exact) return exact;
  } else if (['image', 'video', 'audio', 'document'].includes(row.type)) {
    const persistedBody = row.body?.[row.type] || {};
    const filename = persistedBody.filename;
    const caption  = persistedBody.caption;
    if (filename) {
      const m = candidates.find(c => c[row.type]?.filename === filename);
      if (m) return m;
    }
    if (caption) {
      const m = candidates.find(c => c[row.type]?.caption === caption);
      if (m) return m;
    }
  }

  // Fallback: oldest by timestamp (FIFO — sends are typically processed
  // in submission order).
  return [...candidates].sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))[0];
}
